// utils/creditCardBillPaymentIdempotency.ts
//
// Explicit attempt-lifecycle state machine + canonical payload/fingerprint
// helpers for POST /ar-ap/credit-card-payments (Credit Card Bill Payment:
// Dr Credit Card Payable / Cr Bank-Cash), used by
// CreditCardPayableDetail.tsx -- the only frontend caller of this
// endpoint. Mirrors utils/fixedAssetPaymentIdempotency.ts's proven design
// exactly, adapted to this endpoint's own canonical payload and resource
// identity.
//
// Supersedes an earlier "two independent refs" design (a bare idempotency
// key ref plus a bare submitted-fingerprint ref) that retained both refs
// after EVERY failure, with no way to distinguish a definite backend
// rejection (nothing persisted -- see
// backend/services/idempotency.run_idempotent_operation's claim-before-
// create savepoint, which rolls back the claim along with the business
// writes whenever create() raises) from a genuinely ambiguous one (network
// failure, timeout, or HTTP 500 -- which CAN occur after the payment has
// already committed, since backend/ar_ap_router.py's
// create_credit_card_payment reloads/validates the persisted Journal
// Entry via _load_credit_card_bill_payment AFTER db.commit()). That made
// every definite rejection look identical to an ambiguous one to the
// frontend, permanently blocking a corrected retry as a false "edited
// retry of a possibly-recorded payment".
//
// This module tracks ONE CreditCardBillPaymentAttempt value with an
// explicit `state` -- "idle" | "in-flight" | "ambiguous" -- scoped to the
// company whose Credit Card Payable account the attempt was created for.
// The endpoint itself never takes an account/card identifier in its
// request body: backend/ar_ap_router.py's _credit_card_payable_account
// resolves the ONE `kind == "credit_card_payable"` account for
// current_user.company_id (there is exactly one such account per company
// in this schema -- confirmed by that function's own `.order_by(id.asc())
// .first()` resolution, which has no further filter to pick among
// several). So company_id -- not a card/account id, since none exists as
// a request field -- IS the canonical backend resource this endpoint acts
// on, and is exactly what a stale ambiguous attempt must be scoped to.
// CreditCardPayableDetail.tsx is mounted at a route keyed only by
// `:company_id` (no card/account id segment at all) and is NOT remounted
// by a `:company_id` route-param change alone (same <Route> element, same
// component instance) -- e.g. switching companies via in-app navigation --
// so an ambiguous attempt from a previously viewed company must never be
// silently reused for a different one.

export type CreditCardBillPaymentPayload = {
  date: string;
  bank_account_id: number;
  amount: string;
  memo: string | null;
};

/**
 * Deterministic canonical payload for POST /ar-ap/credit-card-payments.
 * Callers must pass the SAME object (with idempotency_key added) as the
 * actual request body, never a separately reconstructed copy, so the
 * fingerprint can never drift from what is truly sent.
 *
 * - date: the exact submitted YYYY-MM-DD.
 * - bank_account_id: canonicalized to a number so "12" and 12 never
 *   produce different fingerprints.
 * - amount: fixed 2-decimal string (matches the backend's company-scale
 *   quantization for the default/common scale of 2), so "10", "10.0",
 *   "10.00" all canonicalize identically.
 * - memo: trimmed, empty string canonicalized to null -- matching the
 *   backend's own `(payload.get("memo") or "").strip() or None`, so an
 *   omitted/blank memo never looks different from another omitted/blank
 *   memo.
 */
export function canonicalCreditCardBillPayment(
  date: string,
  bankAccountId: string | number,
  amount: string | number,
  memo: string,
): CreditCardBillPaymentPayload {
  return {
    date,
    bank_account_id: Number(bankAccountId),
    amount: Number(amount).toFixed(2),
    memo: memo.trim() || null,
  };
}

export function fingerprintCreditCardBillPayment(
  payload: CreditCardBillPaymentPayload,
): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Attempt lifecycle
// ---------------------------------------------------------------------------

/**
 * - "idle": no active attempt. The next submission always mints a fresh
 *   key.
 * - "in-flight": a request has been sent and its outcome is not yet
 *   known. Transitions to "idle" (success or definite rejection) or
 *   "ambiguous" once the response (or its absence) is classified.
 * - "ambiguous": the last attempt's outcome could not be determined
 *   (network failure, timeout, unreadable response, HTTP 500, or any
 *   other unexpected server error). The key/fingerprint/payload are
 *   retained so an unchanged retry safely replays instead of risking a
 *   duplicate; an edited retry, or a retry for a different company, stays
 *   blocked until this attempt is resolved.
 */
export type CreditCardBillPaymentAttemptState = "idle" | "in-flight" | "ambiguous";

export type CreditCardBillPaymentAttempt = {
  state: CreditCardBillPaymentAttemptState;
  companyId: number | null;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
  submittedPayload: CreditCardBillPaymentPayload | null;
};

export const IDLE_CREDIT_CARD_BILL_PAYMENT_ATTEMPT: CreditCardBillPaymentAttempt = {
  state: "idle",
  companyId: null,
  idempotencyKey: null,
  submittedFingerprint: null,
  submittedPayload: null,
};

export type CreditCardBillPaymentSubmitDecision =
  | { kind: "start"; idempotencyKey: string; attempt: CreditCardBillPaymentAttempt }
  | { kind: "blocked-mismatch" }
  | { kind: "blocked-cross-company" }
  | { kind: "blocked-in-flight" };

/**
 * Pure decision for whether a submit attempt may proceed to the network,
 * given the CURRENT attempt state, the company whose Credit Card Payable
 * is being paid down, and the canonical payload/fingerprint just built
 * from the form. Never mutates `attempt` -- returns a NEW attempt (state
 * "in-flight") for the caller to store when `kind === "start"`.
 *
 *  - idle -> always starts a fresh attempt with a newly generated key.
 *  - ambiguous, SAME company, SAME fingerprint -> unchanged retry: reuses
 *    the stored key exactly (never mints a new one for an ambiguous
 *    outcome -- see module docstring).
 *  - ambiguous, SAME company, DIFFERENT fingerprint -> blocked-mismatch:
 *    the prior attempt under this key may already have committed; an
 *    edited resubmission under the same key must never be sent.
 *  - ambiguous, DIFFERENT company -> blocked-cross-company:
 *    CreditCardPayableDetail.tsx is reused across companies (route param
 *    change, no remount), so a stale ambiguous attempt from a previously
 *    viewed company must never be silently dropped or reused for a
 *    different one.
 *  - in-flight -> blocked-in-flight: defensive. The component's own
 *    synchronous in-flight ref already prevents this from being reached
 *    in practice, but the decision stays correct even if called before
 *    that guard, or from a test, without it.
 */
export function decideCreditCardBillPaymentAttempt(
  attempt: CreditCardBillPaymentAttempt,
  companyId: number,
  payload: CreditCardBillPaymentPayload,
  fingerprint: string,
  generateKey: () => string,
): CreditCardBillPaymentSubmitDecision {
  if (attempt.state === "in-flight") {
    return { kind: "blocked-in-flight" };
  }

  if (attempt.state === "ambiguous") {
    if (attempt.companyId !== companyId) {
      return { kind: "blocked-cross-company" };
    }
    if (attempt.submittedFingerprint !== fingerprint) {
      return { kind: "blocked-mismatch" };
    }
    return {
      kind: "start",
      idempotencyKey: attempt.idempotencyKey as string,
      attempt: { ...attempt, state: "in-flight" },
    };
  }

  const idempotencyKey = generateKey();
  return {
    kind: "start",
    idempotencyKey,
    attempt: {
      state: "in-flight",
      companyId,
      idempotencyKey,
      submittedFingerprint: fingerprint,
      submittedPayload: payload,
    },
  };
}

/** Confirmed 2xx success (create or successful replay -- both return the
 * same response shape, so no distinction is needed here). Clears the
 * attempt back to idle. */
export function resolveCreditCardBillPaymentSuccess(): CreditCardBillPaymentAttempt {
  return IDLE_CREDIT_CARD_BILL_PAYMENT_ATTEMPT;
}

/**
 * Definite pre-commit rejection: any deterministic 4xx this endpoint's
 * stack can produce -- 400 raised by this handler itself (invalid
 * idempotency key, invalid/missing date or amount, non-positive amount,
 * missing/invalid bank_account_id, payment exceeds Credit Card Payable
 * balance, invalid payment account), 404 (Credit Card Payable account not
 * found, bank account not found), 401/403 raised by its auth dependency
 * (get_current_user) before the handler body ever runs, 422 from
 * FastAPI's own request-validation short-circuit, a closed-period 409, or
 * an idempotency-conflict 409. In every one of these cases nothing was
 * persisted under this key (either the handler never ran at all, or
 * create() raised before the claim-insert savepoint could complete --
 * see backend/services/idempotency.run_idempotent_operation), so the
 * identity is free to reuse. Returns to idle so the next submission mints
 * a fresh key and is treated as a new logical attempt, with the user free
 * to edit any field.
 */
export function resolveCreditCardBillPaymentDefiniteRejection(): CreditCardBillPaymentAttempt {
  return IDLE_CREDIT_CARD_BILL_PAYMENT_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before replay validation ran). Retains the key, fingerprint, and
 * submitted payload so a same-company unchanged retry safely replays
 * instead of risking a duplicate; an edited retry -- or a retry against a
 * different company -- stays blocked.
 */
export function resolveCreditCardBillPaymentAmbiguousFailure(
  attempt: CreditCardBillPaymentAttempt,
): CreditCardBillPaymentAttempt {
  return { ...attempt, state: "ambiguous" };
}

/**
 * Whether the payment form for `companyId` should restore the previously
 * submitted values (date/account/amount/memo) instead of resetting to
 * fresh defaults. True only when there is a live AMBIGUOUS attempt for
 * THIS SAME company. CreditCardPayableDetail.tsx uses no modal dialog for
 * its payment form -- it is an always-visible section of the page -- so
 * the moment this applies is when the resolved company_id for the current
 * route changes (the same mounted component being reused for a different
 * company), the equivalent of FixedAssetDetail.tsx's dialog reopen.
 *
 * Restoring matters: resetting to fresh defaults would almost never
 * coincidentally match what was actually submitted, so the very next
 * submission would compute a different fingerprint and get blocked as an
 * "edited retry" -- trapping the user with no visible way to make an
 * unchanged retry that safely reuses the key.
 */
export function shouldRestoreAmbiguousAttempt(
  attempt: CreditCardBillPaymentAttempt,
  companyId: number,
): boolean {
  return attempt.state === "ambiguous" && attempt.companyId === companyId;
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type CreditCardBillPaymentFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Deliberately does NOT classify by HTTP status
 * alone for 409: both a closed-period rejection and an idempotency-
 * conflict rejection share HTTP 409 (see backend/utils/accounting_guards.py's
 * _assert_not_closed, called from assert_year_open at the top of
 * create_credit_card_payment's create() callback, and
 * backend/ar_ap_router.py's own IdempotencyRequestConflict handling) but
 * require different messaging and a different interpretation -- a
 * closed-period 409 is detected via `isClosedPeriod`'s message match; any
 * OTHER 409 is treated as an idempotency conflict, confirmed (by tracing
 * every HTTPException create_credit_card_payment and its own helpers --
 * assert_year_open, _credit_card_payable_account,
 * _account_balance_credit_normal, _require_bank_payment_account -- can
 * raise) to be the ONLY other 409 source this endpoint has -- there is no
 * third 409 variant to disambiguate.
 *
 * Every OTHER 4xx status this endpoint's stack can actually produce --
 * 400 (invalid idempotency key, invalid/missing date, missing/non-
 * positive amount, missing/non-integer bank_account_id, payment exceeds
 * Credit Card Payable balance, invalid payment account) and 404 (Credit
 * Card Payable account not found, bank account not found) raised directly
 * by this handler, 401/403 raised by get_current_user (resolves as a
 * FastAPI dependency BEFORE the handler body runs at all), and 422 from
 * FastAPI's own RequestValidationError handler (also short-circuits
 * before the handler runs) -- is treated as a definite rejection
 * uniformly: every one of these occurs strictly before any business
 * write, so nothing was ever persisted under the key.
 *
 * `status` (added to apiPost's thrown error by api/client.ts) is what
 * distinguishes a genuine HTTP response (4xx/5xx) from a network-level
 * failure: fetch() itself rejecting (network down, DNS, CORS, or a
 * connection-level timeout) throws a plain Error/TypeError with no
 * `.response` at all, so `err?.response?.status` is `undefined` for
 * those -- correctly folded into "ambiguous" here, the same as a
 * malformed/unreadable response. 5xx (500 and any other): ambiguous --
 * _load_credit_card_bill_payment runs AFTER db.commit(), so a 500 can
 * mean the payment WAS persisted and only response construction or
 * replay validation failed. Fails closed for anything not positively
 * identified as a definite pre-commit rejection.
 */
export function classifyCreditCardBillPaymentFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): CreditCardBillPaymentFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }

  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

/**
 * User-facing message shown when a retry is blocked because its details
 * changed since the last attempt under the same idempotency key (see
 * CreditCardPayableDetail.tsx's submitPayment, `decision.kind ===
 * "blocked-mismatch"`).
 *
 * Deliberately does NOT say reloading alone is safe -- a reload proves
 * nothing about whether the prior attempt already posted. Must convey all
 * three ideas: (1) the prior attempt may already have been recorded,
 * (2) restoring the original values permits a safe retry under the same
 * key, (3) the user should check Credit Card Activity before starting a
 * new payment.
 */
export const EDITED_RETRY_BLOCKED_MESSAGE =
  "This payment may already be recorded. Retry with the original details, or check Credit Card Activity below first.";

/**
 * Shown for an idempotency-conflict 409: the current key is permanently
 * bound to a different (already-completed) request and can never succeed
 * for this payload. Deliberately does NOT suggest the payment may
 * ambiguously have been recorded -- that is not the situation; THIS
 * attempt's data was never persisted under this key.
 */
export const CREDIT_CARD_BILL_PAYMENT_CONFLICT_MESSAGE =
  "Couldn't confirm this payment. Check Credit Card Activity before trying again.";

/**
 * Shown when the user tries to submit a payment for a DIFFERENT company
 * while an ambiguous attempt from a previously viewed company is still
 * unresolved (see decideCreditCardBillPaymentAttempt's
 * blocked-cross-company case).
 */
export const CREDIT_CARD_BILL_PAYMENT_CROSS_COMPANY_BLOCKED_MESSAGE =
  "Another company has an unconfirmed card payment. Retry it there first.";
